import { yupResolver } from '@hookform/resolvers/yup';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	Button,
	Card,
	Grid,
	Link,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
	getDisbursementQueryKey,
	useDisbursement,
	useIssueDisbursementMutation,
	useUpdateDisbursementMutation,
} from '../../../api/DisbursementsApi';
import { getUsePolicyQueryKey } from '../../../api/PolicyApi';
import {
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import { Disbursement } from '../../../types/disbursements';
import DisplayCard from '../../DisplayCard';
import DropdownButton, { HiddenActionItem } from '../../DropdownButton';
import Error from '../../Error';
import { FastForm } from '../../fastForm';
import {
	DISBURSEMENT_SPECIAL_FIELDS,
	FORM_NAME,
} from '../../fastForm/data/constants';
import {
	CurrencyField,
	PolicyholderLookupField,
	PolicyLookupField,
	SelectField,
} from '../../fastForm/fields';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import getDisbursementForm, {
	DisbursementFormValues,
} from '../../fastForm/formBuilders/getDisbursementForm';
import { disbursementUpdateBuilder } from '../../fastForm/updateBuilders/disbursementUpdateBuilder';
import { getValidationSchemaWithoutResolver } from '../../fastForm/utils/getValidationSchema';
import ActiveLastBreadcrumb from '../components/Breadcrumbs';
import DisbursementStateChip from '../components/DisbursementStateChip';
import DiscardDisbursementModal from '../components/DiscardDisbursementModal';
import IssueDisbursementModal from '../components/IssueDisbursementModal';
import {
	DISBURSEMENT_PAGE__DATE_CREATED_TITLE,
	DISBURSEMENT_PAGE__DATE_UPDATED_TITLE,
	DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND,
	DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND_ACTION_TEXT,
	DISBURSEMENT_PAGE__DISCARD_BUTTON,
	DISBURSEMENT_PAGE__ISSUE_BUTTON,
	DISBURSEMENT_PAGE__POLICYHOLDER_TITLE,
	DISBURSEMENT_PAGE__POLICY_TITLE,
	DISBURSEMENT_PAGE__SAVE_BUTTON,
	DISBURSEMENT_PAGE__STATUS_TITLE,
	DISBURSEMENT_PAGE__TITLE,
} from '../data/constants';
import { disbursementSchema } from '../lib/disbursementSchema';

interface PolicyResult {
	product: string;
	locator: string;
	status:
		| 'Lapsed'
		| 'In Grace Period'
		| 'Reinstated'
		| 'Withdrawn'
		| 'Cancelled'
		| 'Issued'
		| 'Accepted'
		| 'Application In Progress';
	isIssued: boolean;
}

interface DisbursementPageProps {
	disbursement: Disbursement;
	disableBreadcrumb: boolean;
}

const EditDisbursementPage = (props: DisbursementPageProps) => {
	const authData = useAuthContext();
	const config = useConfigContext();
	const policyData = usePolicyContext();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [isDiscardDisbursementModalOpen, setIsDiscardDisbursementModalOpen] =
		useState(false);
	const [isIssueDisbursementModalOpen, setIsIssueDisbursementModalOpen] =
		useState(false);
	const { timezone, authorizationToken: authToken } = authData.auth;
	const { disbursement, disableBreadcrumb } = props;
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const policyLocator = disbursement?.policyLocator;
	const policyholderLocator = disbursement?.policyholderLocator;
	const createdTimestamp = disbursement?.createdTimestamp;
	const updatedTimestamp = disbursement?.updatedTimestamp;

	const displayId = disbursement?.displayId;
	const state = disbursement?.state;

	const disbursementFields =
		config.cashManagementConfig?.configuration?.disbursement?.fields;

	const form = getDisbursementForm(
		{
			disbursement: disbursement,
			fields: disbursementFields,
			fieldValues: disbursement?.fieldValues || {},
			mediaByLocator: {},
			timezone,
			multiCurrencyEnabled,
		},
		FORM_NAME.disbursement,
	);

	const disbursementFormValues = getDefaultPolicyValues(form, authData);
	const specialFieldValues = {
		amount: disbursement?.amount || '',
		amountCurrency: disbursement?.amountCurrency || '',
		policyholderLocator: disbursement?.policyholderLocator || '',
		policyLocator: disbursement?.policyLocator || '',
	};

	const defaultValues = {
		disbursement: {
			...(disbursementFormValues.disbursement as object),
			...specialFieldValues,
		},
	} as DisbursementFormValues;

	const methods = useForm<DisbursementFormValues>({
		defaultValues,
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			form &&
			yupResolver(
				getValidationSchemaWithoutResolver(form).concat(disbursementSchema()),
			),
	});

	const { formState, control, getValues, reset } = methods;
	const formValues = getValues();
	const { dirtyFields, isDirty } = formState;

	const updateDisbursement = useUpdateDisbursementMutation({
		disbursementLocator: disbursement.locator,
		authToken,
		reset,
		formValues,
		queryClient,
	});

	const issueDisbursement = useIssueDisbursementMutation({
		disbursementLocator: disbursement.locator,
		authToken,
		reset,
		formValues,
		queryClient,
	});

	const refetchData = (policyId?: string) => {
		const policyQueryKey = getUsePolicyQueryKey(policyId, authToken);
		const disbursementQueryKey = getDisbursementQueryKey(
			disbursement.locator,
			authToken,
		);

		queryClient.invalidateQueries(disbursementQueryKey);
		queryClient.invalidateQueries(policyQueryKey);
	};

	const handleDiscardDisbursement = () => {
		updateDisbursement.mutate({
			action: 'discard',
		});
		navigate(`..`, { relative: 'path' });
	};

	const handleSaveDisbursement = () => {
		const values = getValues();
		const updateRequest = disbursementUpdateBuilder(
			dirtyFields,
			values,
			form,
			FORM_NAME.disbursement,
			timezone,
			disbursement.cashBalanceLocator,
		);
		updateDisbursement.mutate({
			disbursementUpdate: updateRequest,
		});
	};

	const handleIssueDisbursement = async () => {
		const values = getValues();
		const updateRequest = disbursementUpdateBuilder(
			dirtyFields,
			values,
			form,
			FORM_NAME.disbursement,
			timezone,
			disbursement.cashBalanceLocator,
		);
		await issueDisbursement.mutateAsync({
			disbursementUpdate: updateRequest,
			action: 'issue',
		});
		refetchData(policyLocator);
		navigate(`..`, { relative: 'path' });
	};

	const formattedModifiedTimestamp =
		authData.getZonedFormattedDateString(updatedTimestamp);

	const formattedCreatedTimestamp =
		authData.getZonedFormattedDateString(createdTimestamp);

	const hiddenActions: HiddenActionItem[] = [];

	const discardAction = {
		label: DISBURSEMENT_PAGE__DISCARD_BUTTON,
		callback: () => setIsDiscardDisbursementModalOpen(true),
		icon: <DeleteIcon />,
	};

	hiddenActions.push(discardAction);

	return (
		<FormProvider {...methods}>
			<Stack spacing={2} alignItems='flex-start'>
				{!disableBreadcrumb && <ActiveLastBreadcrumb />}
				<Stack
					direction='row'
					justifyContent='space-between'
					alignItems='center'
					sx={{ width: '100%' }}
				>
					<Stack direction='row' spacing={2} alignItems='center'>
						<Typography variant='h3'>
							{DISBURSEMENT_PAGE__TITLE} {displayId}
						</Typography>
					</Stack>
					{state === 'draft' && authData.hasPermission('disbursement.update') && (
						<Stack direction='row' spacing={2}>
							<Button
								variant='contained'
								disabled={!isDirty}
								onClick={handleSaveDisbursement}
							>
								{DISBURSEMENT_PAGE__SAVE_BUTTON}
							</Button>
							<Button
								variant='contained'
								onClick={() => setIsIssueDisbursementModalOpen(true)}
							>
								{DISBURSEMENT_PAGE__ISSUE_BUTTON}
							</Button>
							<DropdownButton actions={hiddenActions} />
						</Stack>
					)}
				</Stack>
				<Stack direction='row' spacing={2} alignItems='center'>
					<DisplayCard
						title={DISBURSEMENT_PAGE__POLICYHOLDER_TITLE}
						value={policyholderLocator}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__POLICY_TITLE}
						value={
							<Link component={RouterLink} to={`/policy/${policyLocator}`}>
								{policyLocator || '-'}
							</Link>
						}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__DATE_CREATED_TITLE}
						value={formattedCreatedTimestamp}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__DATE_UPDATED_TITLE}
						value={formattedModifiedTimestamp}
					/>
					<DisplayCard
						title={DISBURSEMENT_PAGE__STATUS_TITLE}
						value={<DisbursementStateChip state={state} />}
					/>
				</Stack>
				<Card sx={{ width: '100%', p: 0 }}>
					<Grid container spacing={2} sx={{ pt: 2, pl: 2, pb: 2 }}>
						<Grid item xs={6}>
							<SelectField<DisbursementFormValues>
								order={0}
								name={`${FORM_NAME.disbursement as 'disbursement'}.${
									DISBURSEMENT_SPECIAL_FIELDS.amountCurrency
								}`}
								title='Currency'
								disabled
								control={control}
								type='select'
								values={[]}
							/>
						</Grid>
						<Grid item xs={6}>
							<CurrencyField
								order={1}
								name={`${FORM_NAME.disbursement as 'disbursement'}.${
									DISBURSEMENT_SPECIAL_FIELDS.amount
								}`}
								title='Amount'
								currency={disbursement.amountCurrency}
								disabled={updateDisbursement.isLoading}
								control={control}
								type='select'
								values={[]}
							/>
						</Grid>
						<Grid item xs={6}>
							<PolicyholderLookupField<DisbursementFormValues>
								policyholderKey={`${FORM_NAME.disbursement}.${DISBURSEMENT_SPECIAL_FIELDS.policyholderLocator}`}
								order={2}
								name={`${FORM_NAME.disbursement as 'disbursement'}.${
									DISBURSEMENT_SPECIAL_FIELDS.policyholderLocator
								}`}
								title='Policyholder'
								disabled
								control={control}
								type='text'
								values={[]}
							/>
						</Grid>
						<Grid item xs={6}>
							<PolicyLookupField<DisbursementFormValues>
								policyKey={`${FORM_NAME.disbursement}.${DISBURSEMENT_SPECIAL_FIELDS.policyLocator}`}
								policyholderKey={`${FORM_NAME.disbursement}.${DISBURSEMENT_SPECIAL_FIELDS.policyholderLocator}`}
								order={3}
								name={`${FORM_NAME.disbursement as 'disbursement'}.${
									DISBURSEMENT_SPECIAL_FIELDS.policyLocator
								}`}
								title='Policy'
								disabled
								policiesFilter={(p: PolicyResult) => p.isIssued} // only allow creating disbursements on issued policies
								control={control}
								type='text'
								values={[]}
							/>
						</Grid>
						<FastForm
							formName={FORM_NAME.disbursement}
							form={form}
							productName={policyData?.productConfig?.name}
							configVersion=''
							authData={authData}
							disabled={updateDisbursement.isLoading}
						/>
					</Grid>
				</Card>
			</Stack>
			<DiscardDisbursementModal
				open={isDiscardDisbursementModalOpen}
				onClose={() => setIsDiscardDisbursementModalOpen(false)}
				onConfirm={() => {
					handleDiscardDisbursement();
					setIsDiscardDisbursementModalOpen(false);
				}}
			/>
			<IssueDisbursementModal
				open={isIssueDisbursementModalOpen}
				onClose={() => setIsIssueDisbursementModalOpen(false)}
				onConfirm={() => {
					handleIssueDisbursement();
					setIsIssueDisbursementModalOpen(false);
				}}
			/>
		</FormProvider>
	);
};

const DisbursementPageLoadingSkeleton = () => {
	return (
		<Stack spacing={2} alignItems='flex-start'>
			<ActiveLastBreadcrumb loading />
			<Stack direction='row' spacing={2} alignItems='center'>
				<Typography variant='h3'>
					<Skeleton variant='text' width={80} />
				</Typography>
			</Stack>
			<Stack direction='row' spacing={2} alignItems='center'>
				<DisplayCard title={DISBURSEMENT_PAGE__POLICYHOLDER_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__POLICY_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__DATE_CREATED_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__DATE_UPDATED_TITLE} loading />
				<DisplayCard title={DISBURSEMENT_PAGE__STATUS_TITLE} loading />
			</Stack>
			<Card sx={{ width: '100%', height: 96 }}></Card>
		</Stack>
	);
};

const EditDisbursementPageWrapper = () => {
	const { disbursementLocator, policyholderId, policyId } = useParams();
	const navigate = useNavigate();
	const authData = useAuthContext();
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const authToken = authData.auth.authorizationToken;
	const {
		data: disbursement,
		isLoading,
		isError,
		error,
	} = useDisbursement(
		disbursementLocator,
		authToken,
		cashManagementEnabled,
		authData.hasPermission('disbursement.fetch'),
	);

	if (isError && axios.isAxiosError(error)) {
		return (
			<Error
				error={
					error?.response?.data.message ||
					`${DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND} ${disbursementLocator}`
				}
				primaryAction={() => navigate('/disbursements')}
				primaryActionText={
					DISBURSEMENT_PAGE__DISBURSEMENT_NOT_FOUND_ACTION_TEXT
				}
			/>
		);
	}

	if (isLoading || !disbursement) {
		return <DisbursementPageLoadingSkeleton />;
	}

	return (
		<EditDisbursementPage
			disbursement={disbursement}
			disableBreadcrumb={!!policyholderId || !!policyId}
		/>
	);
};

export default EditDisbursementPageWrapper;
